// Veranstaltungen (docs/veranstaltungen/SPEZIFIKATION.md): ein eigener Vorgang, der Anfrage, Angebot, Kommissionsauftrag,
// Leihartikel, Lieferung, Abholung und Abrechnung bündelt. Funktionsschalter „Veranstaltungen“ (Vorgabe aus).
//
//   Anfrage → Angebot → Zugesagt → Geliefert → Abgeholt → Abgerechnet · Abgesagt
//
// Mengen der Positionen in Tausendstel des Gebindes (3 Kästen = 3000); Leihartikel in ganzen Stücken × 1000 (10 Garnituren =
// 10000). Tage 'YYYY-MM-DD' (Europe/Berlin), Uhrzeiten 'HH:MM', Geld in Cent.
//
// `sales.event.create` ist zugleich die Schnittstelle der Anfragen-Zentrale (E-Mail-Eingang) und der Bestell-App: Die Eingabe
// wächst nur um optionale Felder.
import { defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { EVENT_LINE_KINDS, EVENT_SOURCES, EVENT_STATUSES, FEE_BASES, RENTAL_CHECK_STATUSES } from '../lib/events'
import { zCustomerRef, zDay, zDocumentRef, zEmptiesEntry, zLocationRef, zNote, zReason, zTime, zUnitQty, zUnitQtyOrZero } from './common'
import { PROOF_BODY_BYTES } from './afterDelivery'
import { zEventProof } from './eventProof'
import { zRentalBooking } from './rental'

/** Funktionsschalter „Veranstaltungen“: ohne ihn gibt es diese Aktionen für den Betrieb nicht */
const FEATURE = 'events'
const READ = 'sales.event.read'
const CREATE = 'sales.event.create'
const WRITE = 'sales.event.write'
const REPORT = 'sales.event.report'
const SETTINGS = 'sales.event.settings'

const zOptText = (max: number) => z.string().trim().max(max, { error: `Höchstens ${max} Zeichen.` }).nullish()

export const zEventContact = z.strictObject({
  name: z.string({ error: 'Bitte einen Namen eingeben.' }).trim().min(1, { error: 'Bitte einen Namen eingeben.' }).max(200),
  company: zOptText(200),
  email: z.email({ error: 'Bitte eine gültige E-Mail-Adresse eingeben.' }).max(200).nullish(),
  phone: zOptText(60),
})

export const zEventVenue = z.strictObject({
  street: zOptText(200),
  postalCode: zOptText(20),
  city: zOptText(120),
  /** Hinweis zum Ort: Zufahrt, Abladestelle, Ansprechpartner vor Ort */
  note: zOptText(500),
})

/** Getränke und Ware (auf Kommission): Artikel und Menge im Gebinde in Tausendstel; ohne Gebinde das Verkaufsgebinde */
export const zEventGoodsInput = z.strictObject({ itemId: zId, qty: zUnitQty, unitId: zId.nullish(), note: zNote })
/** Leihartikel: ganze Stücke in Tausendstel (10 Garnituren = 10000) */
export const zEventRentalInput = z.strictObject({ itemId: zId, qty: zUnitQty.multipleOf(1000, { error: 'Leihartikel werden in ganzen Stücken angefragt.' }), note: zNote })
/** Gebühr: Dienstleistungsartikel und Menge in Tausendstel (1 Pauschale = 1000) */
export const zEventFeeInput = z.strictObject({ itemId: zId, qty: zUnitQty, note: zNote })

const timeFields = {
  eventStartTime: zTime.nullish(),
  eventEndTime: zTime.nullish(),
  deliveryTimeFrom: zTime.nullish(),
  deliveryTimeTo: zTime.nullish(),
  pickupTimeFrom: zTime.nullish(),
  pickupTimeTo: zTime.nullish(),
}

// ---- Ansicht --------------------------------------------------------------------------------------------------------

export const zEventRentalCheck = z.object({
  /** verfügbar · knapp · nicht verfügbar · Zeitraum fehlt */
  status: z.enum(RENTAL_CHECK_STATUSES),
  statusLabel: z.string(),
  fromDate: z.string().nullable(),
  toDate: z.string().nullable(),
  /** Bestand des Leihartikels am Heimatstandort (Tausendstel) */
  totalQty: z.int(),
  /** kleinste freie Menge im Zeitraum ohne die eigenen Buchungen dieser Veranstaltung (Tausendstel) */
  freeQty: z.int(),
  /** bei Partnern zugeliehen – nur für diese Veranstaltung (Tausendstel) */
  loanQty: z.int(),
  /** so viel fehlt: gewünscht − frei − zugeliehen, nie negativ (Tausendstel) */
  shortQty: z.int(),
  /** Satz für Menschen: „Am 12.11.2026 sind nur 6 St frei – 4 St fehlen.“ */
  message: z.string(),
})

export const zEventLine = z.object({
  id: zId,
  pos: z.int(),
  kind: z.enum(EVENT_LINE_KINDS),
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  unitId: zId.nullable(),
  /** Kurzform des Gebindes: „Ka“, „Fl“, „St“ */
  unit: z.string(),
  qty: z.int(),
  note: z.string().nullable(),
  /** nur Leihartikel: Prüfung der Verfügbarkeit im Zeitraum der Veranstaltung */
  rental: zEventRentalCheck.nullable(),
})

// ---- Abholung (erfasst, noch nicht gebucht) --------------------------------------------------------------------------

/** Nachweis bei Übergabe und Abholung: Unterschrift und/oder Foto (Format wie der Zustellnachweis, contract/eventProof.ts) */
export { EVENT_PROOF_LIMITS, zEventProof } from './eventProof'

/** volle Rückware je Position der Kommissionslieferung (Tausendstel des Gebindes der Zeile) */
export const zEventReturnLine = z.strictObject({ lineId: zId, returnedQty: zUnitQtyOrZero })
/** Leihartikel je Buchung: zurück (davon beschädigt), Rest fehlt – ganze Stücke × 1000 */
export const zEventRentalReturn = z.strictObject({
  bookingId: zId,
  returnedQty: zUnitQtyOrZero.multipleOf(1000, { error: 'Leihartikel werden in ganzen Stücken gezählt.' }),
  damagedQty: zUnitQtyOrZero.multipleOf(1000, { error: 'Leihartikel werden in ganzen Stücken gezählt.' }).default(0),
  note: zNote,
})

export const zEventPickup = z.object({
  at: z.date(),
  byName: z.string().nullable(),
  /** office = im Büro erfasst · driver = Fahrer (Telefon-Ansicht) · app = Bestell-App */
  source: z.enum(['office', 'driver', 'app']),
  lines: z.array(z.object({ lineId: zId, returnedQty: z.int() })),
  empties: z.array(z.object({ depositTypeId: zId, emptiesQty: z.int() })),
  rentals: z.array(z.object({ bookingId: zId, returnedQty: z.int(), damagedQty: z.int(), note: z.string().nullable() })),
  note: z.string().nullable(),
  /** Datei der Unterschrift (/api/files/<id>) */
  proofFileId: zId.nullable(),
  signedBy: z.string().nullable(),
  /** Datei des Fotos (/api/files/<id>) */
  photoFileId: zId.nullable(),
})

/** Zuleihe von einem Partner – nur für diese Veranstaltung (M5, sales.event.addLoan) */
export const zEventLoan = z.object({
  id: zId,
  itemId: zId,
  itemName: z.string(),
  partner: z.object({ id: zId, number: z.string().nullable(), name: z.string() }),
  qty: z.int(),
  fromDate: z.string(),
  toDate: z.string(),
  /** Kosten der Zuleihe (netto, Vermerk – gebucht wird nichts) */
  costCents: z.int().nullable(),
  note: z.string().nullable(),
  createdAt: z.date(),
  createdBy: z.string().nullable(),
})

export const zEventDelivery = z.object({
  at: z.date(),
  byName: z.string().nullable(),
  source: z.enum(['office', 'driver', 'app']),
  receivedBy: z.string().nullable(),
  note: z.string().nullable(),
  /** Datei der Unterschrift (/api/files/<id>) */
  proofFileId: zId.nullable(),
  signedBy: z.string().nullable(),
  /** Datei des Fotos (/api/files/<id>) */
  photoFileId: zId.nullable(),
})

export const zEventView = z.object({
  id: zId,
  version: z.int(),
  number: z.string(),
  status: z.enum(EVENT_STATUSES),
  statusLabel: z.string(),
  location: zLocationRef,
  source: z.enum(EVENT_SOURCES),
  sourceLabel: z.string(),
  sourceRef: z.string().nullable(),
  customer: zCustomerRef.nullable(),
  contact: z.object({ name: z.string(), company: z.string().nullable(), email: z.string().nullable(), phone: z.string().nullable() }).nullable(),
  title: z.string(),
  venue: z.object({ street: z.string().nullable(), postalCode: z.string().nullable(), city: z.string().nullable(), note: z.string().nullable() }).nullable(),
  /** Anschrift in einer Zeile */
  venueText: z.string().nullable(),
  eventStart: z.string().nullable(),
  eventStartTime: z.string().nullable(),
  eventEnd: z.string().nullable(),
  eventEndTime: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  deliveryTimeFrom: z.string().nullable(),
  deliveryTimeTo: z.string().nullable(),
  pickupDate: z.string().nullable(),
  pickupTimeFrom: z.string().nullable(),
  pickupTimeTo: z.string().nullable(),
  /** Zeitraum der Leihartikel: Anlieferung bis Abholung (beide Tage gebunden); null ohne Termine */
  rentalPeriod: z.object({ fromDate: z.string(), toDate: z.string() }).nullable(),
  guests: z.int().nullable(),
  notes: z.string().nullable(),
  /** Positionen der Anfrage (vor der Zusage änderbar) */
  lines: z.array(zEventLine),
  /** Belege des Vorgangs: Angebot, Auftrag, Kommissionslieferung, Abrechnung (Lieferschein), Rücknahmen */
  /** Belege des Vorgangs; `pickup` = Abholauftrag (AH-…, entsteht mit der Übergabe) */
  documents: z.array(zDocumentRef.extend({ role: z.enum(['quote', 'order', 'consignment', 'pickup', 'settlement', 'return', 'empties']) })),
  /** Leihbuchungen der Veranstaltung (ab der Zusage): Zeitraum, Stück, Zustand, Rückgabe */
  bookings: z.array(zRentalBooking),
  /** der Auftrag wartet auf eine Freigabe (Rabatt, Kreditlimit) – die Zusage ist erst danach vollständig; sonst null */
  orderHold: z.string().nullable(),
  /** erfasste Abholung (Mengen, Leergut, Leihartikel, Nachweis) – noch nicht gebucht; null vor der Abholung */
  pickup: zEventPickup.nullable(),
  /** bestätigte Übergabe beim Kunden (Fahrer, Bestell-App, Büro) – null ohne Bestätigung (z. B. „Lieferung buchen“ mit Übergabe) */
  delivery: zEventDelivery.nullable(),
  /** Zuleihen von Partnern (nicht zurückgenommen) – sie zählen nur für diese Veranstaltung zur Verfügbarkeit */
  loans: z.array(zEventLoan),
  /** Bruttobetrag des Lieferscheins der Abrechnung (Verbrauch, Leihgebühren, Gebühren, Ersatz) – null vor der Abrechnung */
  settlementGrossCents: z.int().nullable(),
  cancelReason: z.string().nullable(),
  deliveredAt: z.date().nullable(),
  pickedUpAt: z.date().nullable(),
  settledAt: z.date().nullable(),
  cancelledAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: z.string().nullable(),
  /** was der Aufrufer jetzt tun kann */
  allowed: z.object({
    edit: z.boolean(),
    editLines: z.boolean(),
    assignCustomer: z.boolean(),
    createQuote: z.boolean(),
    confirm: z.boolean(),
    deliver: z.boolean(),
    recordPickup: z.boolean(),
    settle: z.boolean(),
    cancel: z.boolean(),
    /** Übergabe bestätigen (Lieferung gebucht, noch nicht übergeben) */
    handover: z.boolean(),
  }),
  /** nächster Schritt als Satz („Kunde fehlt – bitte zuordnen oder anlegen.“) */
  nextStep: z.string().nullable(),
})
export type EventView = z.output<typeof zEventView>

export const zEventRow = z.object({
  id: zId,
  number: z.string(),
  status: z.enum(EVENT_STATUSES),
  statusLabel: z.string(),
  title: z.string(),
  customerName: z.string(),
  customer: zCustomerRef.nullable(),
  locationCode: z.string(),
  source: z.enum(EVENT_SOURCES),
  eventStart: z.string().nullable(),
  eventEnd: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  pickupDate: z.string().nullable(),
  venueText: z.string().nullable(),
  guests: z.int().nullable(),
  /** Zahl der Positionen je Art */
  goodsCount: z.int(),
  rentalCount: z.int(),
  /** Leihartikel, die im Zeitraum knapp oder nicht verfügbar sind (nur vor der Zusage geprüft) */
  rentalWarning: z.enum(['tight', 'unavailable']).nullable(),
  quoteNumber: z.string().nullable(),
  orderNumber: z.string().nullable(),
  updatedAt: z.date(),
})

// ---- Anlegen, ändern ------------------------------------------------------------------------------------------------

export const eventCreate = defineContract({
  name: 'sales.event.create',
  title: 'Veranstaltung anlegen',
  description: 'Erfasst eine Anfrage als Veranstaltung: Kunde oder Interessent, Anlass, Ort, Termine, Gäste und gewünschte Positionen.',
  llm: {
    description:
      'Legt eine Veranstaltung im Zustand „Anfrage“ an und vergibt die Nummer (VA-JJJJ-00001). Pflicht: `source` (manual, mail, phone, app), ' +
      '`title` (Anlass) und ein Kunde (`customerId`) ODER ein Interessent (`contact.name`). Termine als Tage: Anlieferung (`deliveryDate`), ' +
      'Beginn/Ende (`eventStart`, `eventEnd`), Abholung (`pickupDate`) – die Leihartikel sind von der Anlieferung bis zur Abholung gebunden. ' +
      '`lines`: Getränke auf Kommission, Menge im Gebinde in Tausendstel (3 Kästen = 3000); `rentals`: Leihartikel in Stück × 1000 ' +
      '(10 Garnituren = 10000). Reserviert noch nichts – die Verfügbarkeit der Leihartikel zeigt sales.event.get. Danach: Angebot ' +
      '(sales.event.createQuote), Zusage (sales.event.confirm).',
    keywords: ['Veranstaltung', 'Fest', 'Festservice', 'Anfrage', 'Event', 'Feier', 'Firmenfest', 'Vereinsfest', 'Hochzeit'],
  },
  input: z.strictObject({
    source: z.enum(EVENT_SOURCES, { error: 'Bitte die Quelle angeben (von Hand, E-Mail, Telefon, Bestell-App).' }),
    sourceRef: zOptText(200),
    customerId: zId.nullish(),
    contact: zEventContact.nullish(),
    title: z.string({ error: 'Bitte den Anlass eingeben.' }).trim().min(1, { error: 'Bitte den Anlass eingeben.' }).max(200),
    venue: zEventVenue.nullish(),
    eventStart: zDay.nullish(),
    eventEnd: zDay.nullish(),
    deliveryDate: zDay.nullish(),
    pickupDate: zDay.nullish(),
    guests: z.int({ error: 'Bitte eine ganze Zahl eingeben.' }).min(0).max(1_000_000).nullish(),
    notes: zOptText(4000),
    lines: z.array(zEventGoodsInput).max(200).optional(),
    rentals: z.array(zEventRentalInput).max(100).optional(),
    // ---- optional (additiv) ----
    /** liefernder Standort; ohne Angabe das Depot der Disposition bzw. das erste Lager */
    locationId: zId.nullish(),
    ...timeFields,
    /** Gebühren schon in der Anfrage (Anfahrt, Kühlung): Dienstleistungsartikel */
    fees: z.array(zEventFeeInput).max(20).optional(),
  }),
  output: z.object({ id: zId, number: z.string() }),
  permission: CREATE,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
  effects: { reversible: true },
  limits: { maxItems: 320, itemCount: (input) => (input.lines?.length ?? 0) + (input.rentals?.length ?? 0) + (input.fees?.length ?? 0) + 1 },
})

export const eventUpdate = defineContract({
  name: 'sales.event.update',
  title: 'Veranstaltung ändern',
  description: 'Ändert Anlass, Kunde oder Interessent, Ort, Termine, Gäste, Notiz und – vor der Zusage – die Positionen.',
  llm: {
    description:
      'Ändert eine Veranstaltung: nur die genannten Felder (`null` leert ein freiwilliges Feld). Termine, Ort und Positionen nur vor der ' +
      'Zusage (Anfrage, Angebot) – danach führt der Auftrag. `lines`, `rentals` und `fees` ersetzen die jeweilige Liste ganz. Ein schon ' +
      'erstelltes Angebot ändert sich dadurch nicht (es ist ein eigener Beleg).',
    keywords: ['Veranstaltung ändern', 'Termin verschieben', 'Gäste', 'Positionen ändern'],
  },
  input: z.strictObject({
    id: zId,
    expectedVersion: z.int().positive().optional(),
    title: z.string().trim().min(1, { error: 'Bitte den Anlass eingeben.' }).max(200).optional(),
    customerId: zId.nullish(),
    contact: zEventContact.nullish(),
    venue: zEventVenue.nullish(),
    eventStart: zDay.nullish(),
    eventEnd: zDay.nullish(),
    deliveryDate: zDay.nullish(),
    pickupDate: zDay.nullish(),
    ...timeFields,
    guests: z.int({ error: 'Bitte eine ganze Zahl eingeben.' }).min(0).max(1_000_000).nullish(),
    notes: zOptText(4000),
    sourceRef: zOptText(200),
    locationId: zId.optional(),
    lines: z.array(zEventGoodsInput).max(200).optional(),
    rentals: z.array(zEventRentalInput).max(100).optional(),
    fees: z.array(zEventFeeInput).max(20).optional(),
  }),
  output: zEventView,
  permission: WRITE,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
  limits: { maxItems: 320, itemCount: (input) => (input.lines?.length ?? 0) + (input.rentals?.length ?? 0) + (input.fees?.length ?? 0) + 1 },
})

export const eventAssignCustomer = defineContract({
  name: 'sales.event.assignCustomer',
  title: 'Kunden einer Veranstaltung zuordnen',
  description: 'Ordnet der Anfrage einen vorhandenen Kunden zu – oder legt den Interessenten als neuen Kunden an.',
  llm: {
    description:
      'Ordnet einer Veranstaltung einen Kunden zu: `customerId` (vorhandener Kunde) ODER `create` – dann entsteht aus dem Interessenten ' +
      '(Name, Firma, E-Mail, Telefon) ein neuer Kunde (Privatperson oder Unternehmen). Ein Angebot braucht einen Kunden.',
    keywords: ['Kunde zuordnen', 'Interessent anlegen', 'Kunde anlegen', 'neuer Kunde'],
  },
  input: z
    .strictObject({ id: zId, customerId: zId.optional(), create: z.strictObject({ type: z.enum(['private', 'business']) }).optional() })
    .refine((input) => (input.customerId === undefined) !== (input.create === undefined), { error: 'Bitte einen Kunden wählen oder den Interessenten anlegen.', path: ['customerId'] }),
  output: zEventView,
  permission: WRITE,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
})

export const eventCancel = defineContract({
  name: 'sales.event.cancel',
  title: 'Veranstaltung absagen',
  description: 'Sagt eine Veranstaltung ab: Leihbuchungen werden frei, ein bestätigter Auftrag wird abgebrochen, das Angebot gilt als abgelehnt.',
  llm: {
    description:
      'Sagt eine Veranstaltung vor der Lieferung ab (Anfrage, Angebot, Zugesagt). Gebuchte Leihartikel werden frei, ein noch nicht ' +
      'gelieferter Auftrag wird abgebrochen (Reservierungen frei), ein offenes Angebot als abgelehnt vermerkt. Braucht einen Grund. ' +
      'Nach der Lieferung geht das nicht mehr – dann abholen und abrechnen.',
    keywords: ['absagen', 'Veranstaltung absagen', 'Fest abgesagt', 'stornieren'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zEventView,
  permission: WRITE,
  feature: FEATURE,
  risk: 'destructive',
  scope: 'record',
})

// ---- Angebot, Zusage, Lieferung (M2) --------------------------------------------------------------------------------

export const eventCreateQuote = defineContract({
  name: 'sales.event.createQuote',
  title: 'Angebot zur Veranstaltung erstellen',
  description: 'Erstellt aus den Positionen der Anfrage ein Angebot (Entwurf): Getränke auf Kommission, Leihgebühren für den Zeitraum, Gebühren.',
  llm: {
    description:
      'Erstellt zu einer Veranstaltung mit Kunde ein Angebot als Entwurf (Belegart Angebot): Getränke als Artikelzeilen, je Leihartikel eine ' +
      'Gebührenzeile „Leihgebühr …“ zum Preis der Preisfindung (Preis je Veranstaltung), Gebühren als Dienstleistungen. Kopf: Auftragsart ' +
      'Kommission, Liefertermin = Anlieferung, Tag der Rückzählung = Abholung, Lieferanschrift = Veranstaltungsort. Danach im Angebot ' +
      'Preise prüfen, „Angebot abgeben“ und per E-Mail senden (sales.quote.post, sales.quote.send). Die Veranstaltung steht dann auf „Angebot“.',
    keywords: ['Angebot Fest', 'Festangebot', 'Angebot erstellen', 'Kostenvoranschlag Veranstaltung'],
  },
  input: z.strictObject({ id: zId, validUntil: zDay.nullish() }),
  output: zEventView,
  permission: WRITE,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
})

export const eventConfirm = defineContract({
  name: 'sales.event.confirm',
  title: 'Zusage der Veranstaltung erfassen',
  description: 'Der Kunde hat zugesagt: Kommissionsauftrag aus dem Angebot bestätigen (reserviert die Ware) und die Leihartikel für den Zeitraum fest buchen.',
  llm: {
    description:
      'Erfasst die Zusage des Kunden: Aus dem Angebot (sonst aus den Positionen) entsteht ein Auftrag der Art Kommission, der bestätigt ' +
      'wird (Reservierung, Rabatt- und Kreditprüfung wie bei jedem Auftrag – wartet er auf eine Freigabe, ist die Zusage erst danach ' +
      'vollständig; dann erneut aufrufen). Die Leihartikel werden für den Zeitraum Anlieferung bis Abholung fest gebucht – reicht der ' +
      'Bestand nicht, wird gar nichts gebucht und der Satz nennt die Fehlmenge (Ausweg: Zuleihe beim Partner oder Menge kürzen). ' +
      'Danach erscheint die Lieferung in der Disposition.',
    keywords: ['Zusage', 'Kunde hat zugesagt', 'Auftrag bestätigen', 'Fest bestätigen', 'Leihartikel buchen'],
  },
  input: z.strictObject({ id: zId, note: zNote }),
  output: zEventView,
  permission: WRITE,
  feature: FEATURE,
  risk: 'financial',
  scope: 'record',
})

export const eventDeliver = defineContract({
  name: 'sales.event.deliver',
  title: 'Lieferung der Veranstaltung buchen',
  description: 'Bucht die Kommissionslieferung (Ware zum Kunden, berechnet wird noch nichts) und gibt die gebuchten Leihartikel aus.',
  llm: {
    description:
      'Liefert eine zugesagte Veranstaltung aus: Die offenen Getränke gehen per Kommissionslieferung an den Standort „Kommission beim ' +
      'Kunden“, die gebuchten Leihartikel werden ausgegeben (bei Seriennummern je Buchung die Nummern in `serials`). Mit `handedOver` ' +
      '(Standard) gilt die Übergabe als erfolgt – die Veranstaltung steht auf „Geliefert“, ein Stopp in der Disposition auf ' +
      '„zugestellt“. `handedOver: false` bucht nur (Ware verladen); die Übergabe bestätigt dann der Fahrer (sales.event.handover).',
    keywords: ['Lieferung Fest', 'ausliefern', 'Kommission liefern', 'Leihartikel ausgeben', 'Fest beliefern'],
  },
  input: z.strictObject({
    id: zId,
    serials: z.array(z.strictObject({ bookingId: zId, serialNos: z.array(z.string().trim().min(1).max(80)).max(200) })).max(100).optional(),
    handedOver: z.boolean().default(true),
    documentDate: zDay.optional(),
    note: zNote,
  }),
  output: zEventView,
  permission: WRITE,
  feature: FEATURE,
  risk: 'financial',
  scope: 'record',
})

// ---- Abholung und Abrechnung (M3) -----------------------------------------------------------------------------------

export const eventRecordPickup = defineContract({
  name: 'sales.event.recordPickup',
  title: 'Abholung einer Veranstaltung erfassen',
  description: 'Hält fest, was bei der Abholung zurückkommt: volle Rückware je Position, Leergut je Pfandart, Leihartikel zurück, beschädigt oder fehlend – gebucht wird erst mit der Abrechnung.',
  llm: {
    description:
      'Erfasst die Abholung einer gelieferten Veranstaltung (Fahrer oder Büro): je Position der Kommissionslieferung die vollen ' +
      'Gebinde, die zurückkommen (`lines`, Tausendstel des Gebindes), Leergut je Pfandart (`empties`, Stück × 1000), je ' +
      'Leihbuchung zurück und davon beschädigt (`rentals`, Stück × 1000; der Rest fehlt), optional die Unterschrift. Die ' +
      'Veranstaltung steht danach auf „Abgeholt“; erneut erfasst ersetzt die Angaben. Gebucht wird nichts – das tut ' +
      'sales.event.settle.',
    keywords: ['Abholung', 'Rückware', 'Leergut zählen', 'Fest abholen', 'zurückgekommen', 'Leihartikel zurück'],
  },
  input: z.strictObject({
    id: zId,
    lines: z.array(zEventReturnLine).max(500).default([]),
    empties: z.array(zEmptiesEntry).max(50).default([]),
    rentals: z.array(zEventRentalReturn).max(100).default([]),
    note: zNote,
    proof: zEventProof.nullish(),
    /** Stopp der Abholung auf einer Tour (die Disposition setzt ihn auf „erledigt“) */
    stopId: zId.nullish(),
  }),
  output: zEventView,
  permission: REPORT,
  feature: FEATURE,
  risk: 'write',
  scope: 'record',
  limits: { maxItems: 650, itemCount: (input) => input.lines.length + input.empties.length + input.rentals.length + 1, bodyBytes: PROOF_BODY_BYTES },
})

export const zEventSettlement = z.object({
  id: zId,
  number: z.string(),
  status: z.enum(EVENT_STATUSES),
  /** Preisbasis des Auftrags: Gebühren und Ersatz stehen in ihr */
  priceMode: z.enum(['gross', 'net']),
  consignmentId: zId.nullable(),
  /** Positionen der Kommissionslieferung mit dem Vorschlag der vollen Rückware (aus der erfassten Abholung) */
  lines: z.array(
    z.object({ lineId: zId, itemId: zId.nullable(), sku: z.string().nullable(), name: z.string(), unit: z.string().nullable(), unitCode: z.string().nullable(), deliveredQty: z.int(), outQty: z.int(), returnedQty: z.int() }),
  ),
  /** Pfandarten der gelieferten Gebinde mit dem Vorschlag des Leerguts (Stück × 1000) */
  depositTypes: z.array(z.object({ depositTypeId: zId, name: z.string(), emptiesQty: z.int() })),
  /** Leihbuchungen beim Kunden mit Vorschlag (zurück, beschädigt) und Ersatzpreis je Stück in der Preisbasis */
  rentals: z.array(
    z.object({
      bookingId: zId,
      itemId: zId,
      name: z.string(),
      unit: z.string(),
      serialTracked: z.boolean(),
      qty: z.int(),
      returnedQty: z.int(),
      damagedQty: z.int(),
      replacementPriceCents: z.int().nullable(),
      taxCodeId: zId.nullable(),
    }),
  ),
  /** Gebührenregeln mit Preis je Einheit in der Preisbasis (null: kein Preis gefunden) */
  rules: z.array(
    z.object({
      ruleId: zId,
      itemId: zId,
      label: z.string(),
      basis: z.enum(FEE_BASES),
      unitCodes: z.array(z.string()),
      unitPriceCents: z.int().nullable(),
      /** rule = Preis der Regel · zone = Liefergebiet der Anschrift · pricing = Preisfindung · none = kein Preis */
      priceSource: z.enum(['rule', 'zone', 'pricing', 'none']),
    }),
  ),
  /** Zahl der Fahrten für Gebühren „je Fahrt“ */
  trips: z.int(),
  pickup: zEventPickup.nullable(),
  canSettle: z.boolean(),
})

export const eventSettlement = defineContract({
  name: 'sales.event.settlement',
  title: 'Abrechnung einer Veranstaltung vorbereiten',
  description: 'Vorschlag für die Abrechnung: Rückware je Position, Leergut, Leihartikel mit Ersatzpreis, Gebühren nach den Regeln.',
  llm: {
    description:
      'Liefert den Vorschlag zur Abrechnung einer gelieferten Veranstaltung: je Position geliefert und volle Rückware (aus der ' +
      'erfassten Abholung), Leergut je Pfandart, Leihbuchungen mit Rückgabe und Ersatzpreis je Stück, die Gebührenregeln mit Preis je ' +
      'Einheit in der Preisbasis des Auftrags und die Zahl der Fahrten. Ändert nichts – abgerechnet wird mit sales.event.settle.',
    keywords: ['Abrechnung vorbereiten', 'Rückzählung', 'Gebühren', 'Ersatz', 'Fehlmengen'],
  },
  input: z.strictObject({ id: zId }),
  output: zEventSettlement,
  permission: READ,
  feature: FEATURE,
  risk: 'read',
  scope: 'record',
})

export const eventSettle = defineContract({
  name: 'sales.event.settle',
  title: 'Veranstaltung abrechnen',
  description: 'Bucht die Rücknahme (volle Ware, Leergut, Leihartikel) und macht Verbrauch, Leihgebühren, Gebühren und Ersatz als Lieferschein abrechenbar.',
  llm: {
    description:
      'Rechnet eine gelieferte Veranstaltung ab – in einem Schritt: Leihartikel zurücknehmen (`rentals`: zurück, beschädigt; der Rest ' +
      'fehlt), volle Rückware buchen (`lines`; ohne `lines`, `empties`, `rentals` gilt die erfasste Abholung), der Verbrauch (geliefert − zurück) wird mit den Leihgebühren des Auftrags, den ' +
      'Gebühren (`fees`: Dienstleistungsartikel, Menge in Tausendstel, Preis je Einheit in der Preisbasis des Auftrags) und dem Ersatz ' +
      'für Fehlendes oder Beschädigtes (`charges`) als zugestellter Lieferschein abrechenbar; Leergut (`empties`) wird dem Kunden ' +
      'gutgeschrieben. Danach „Abgerechnet“; die Rechnung entsteht im Rechnungslauf. Vorschlag vorher: sales.event.settlement.',
    keywords: ['abrechnen', 'Endabrechnung', 'Fest abrechnen', 'Verbrauch berechnen', 'Gebühren', 'Ersatz berechnen'],
  },
  input: z.strictObject({
    id: zId,
    /** volle Rückware je Position; ohne Angabe gilt die erfasste Abholung (ohne Erfassung: nichts zurück) */
    lines: z.array(zEventReturnLine).max(500).optional(),
    /** Leergut je Pfandart; ohne Angabe gilt die erfasste Abholung (ohne Erfassung: keins) */
    empties: z.array(zEmptiesEntry).max(50).optional(),
    /** je Leihbuchung zurück/beschädigt; fehlt eine Buchung, gilt die erfasste Abholung, sonst „vollständig zurück“ */
    rentals: z.array(zEventRentalReturn.extend({ condition: z.string().trim().max(500).nullish() })).max(100).optional(),
    fees: z
      .array(
        z.strictObject({
          /** Dienstleistungsartikel der Gebühr (Steuerschlüssel, Bezeichnung) */
          itemId: zId,
          qty: zUnitQty,
          /** Preis je Einheit in der Preisbasis des Auftrags (Cent) */
          unitPriceCents: z.int({ error: 'Bitte einen Betrag eingeben.' }).min(0, { error: 'Der Preis darf nicht negativ sein.' }).max(10_000_000),
          /** Bezeichnung auf dem Lieferschein; ohne Angabe die der Regel bzw. des Artikels */
          label: z.string().trim().max(120).nullish(),
          note: zNote,
        }),
      )
      .max(20)
      .default([]),
    charges: z
      .array(
        z.strictObject({
          description: z.string({ error: 'Bitte eine Bezeichnung eingeben.' }).trim().min(2, { error: 'Bitte eine Bezeichnung eingeben.' }).max(200),
          qty: zUnitQty,
          unitPriceCents: z.int({ error: 'Bitte einen Betrag eingeben.' }).min(0, { error: 'Der Preis darf nicht negativ sein.' }).max(100_000_000),
          /** Artikel, dessen Steuerschlüssel gilt (der Leihartikel) */
          itemId: zId,
        }),
      )
      .max(50)
      .default([]),
    documentDate: zDay.optional(),
    note: zNote,
  }),
  output: zEventView,
  permission: WRITE,
  feature: FEATURE,
  risk: 'financial',
  scope: 'record',
  limits: { maxItems: 750, itemCount: (input) => (input.lines?.length ?? 0) + (input.empties?.length ?? 0) + (input.rentals?.length ?? 0) + input.fees.length + input.charges.length + 1 },
})

// ---- Einstellungen: Gebührenregeln, Anbindung der Bestell-App (M3/M4) ----------------------------------------------------

export const zEventFeeRule = z.object({
  id: zId,
  itemId: zId,
  itemName: z.string(),
  label: z.string(),
  basis: z.enum(FEE_BASES),
  basisLabel: z.string(),
  unitCodes: z.array(z.string()),
  priceCents: z.int().nullable(),
  priceMode: z.enum(['gross', 'net']),
  useZoneFee: z.boolean(),
  sort: z.int(),
  active: z.boolean(),
})

export const eventSettings = defineContract({
  name: 'sales.event.settings',
  title: 'Einstellungen der Veranstaltungen anzeigen',
  description: 'Gebührenregeln (Rückgaben, Anfahrt, Pauschalen) und ob die Bestell-App Veranstaltungen kennt.',
  llm: {
    description: 'Liefert die Gebührenregeln der Veranstaltungen (Dienstleistungsartikel, Grundlage: pauschal, je Fahrt oder je zurückgegebenem Gebinde, Preis oder Liefergebiet) und den Schalter, ob die Bestell-App Lieferungen und Abholungen von Veranstaltungen bekommt.',
    keywords: ['Gebühren Veranstaltung', 'Bearbeitungsgebühr', 'Anfahrt', 'Gebührenregeln'],
  },
  input: z.strictObject({}),
  output: z.object({ rules: z.array(zEventFeeRule), channelEvents: z.boolean(), unitCodes: z.array(z.object({ code: z.string(), label: z.string() })) }),
  permission: READ,
  feature: FEATURE,
  risk: 'read',
  scope: 'any',
})

export const eventSaveSettings = defineContract({
  name: 'sales.event.saveSettings',
  title: 'Einstellungen der Veranstaltungen speichern',
  description: 'Legt die Gebührenregeln fest (die ganze Liste) und schaltet die Anbindung der Bestell-App an Veranstaltungen.',
  llm: {
    description:
      'Speichert die Gebührenregeln als ganze Liste (fehlende werden entfernt): je Regel ein Dienstleistungsartikel, Grundlage (flat = je ' +
      'Veranstaltung, per_trip = je Fahrt, per_returned_unit = je zurückgegebenem vollen Gebinde, wahlweise nur Einheiten wie crate, keg), ' +
      'optional Preis in Cent (brutto oder netto) oder Gebühr des Liefergebiets. `channelEvents` schaltet, ob die Bestell-App Lieferungen ' +
      'und Abholungen von Veranstaltungen bekommt (nur, wenn ihre Fassung sie kennt).',
    keywords: ['Gebührenregeln festlegen', 'Bearbeitungsgebühr festlegen', 'Anfahrt Gebühr'],
  },
  input: z.strictObject({
    rules: z
      .array(
        z.strictObject({
          itemId: zId,
          label: z.string().trim().max(120).nullish(),
          basis: z.enum(FEE_BASES),
          unitCodes: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
          priceCents: z.int({ error: 'Bitte einen Betrag eingeben.' }).min(0, { error: 'Der Preis darf nicht negativ sein.' }).max(10_000_000).nullish(),
          priceMode: z.enum(['gross', 'net']).default('gross'),
          useZoneFee: z.boolean().default(false),
          active: z.boolean().default(true),
        }),
      )
      .max(30)
      .optional(),
    channelEvents: z.boolean().optional(),
  }),
  output: eventSettings.output,
  permission: SETTINGS,
  feature: FEATURE,
  risk: 'write',
  effects: { reversible: true },
})

// ---- Lesen ----------------------------------------------------------------------------------------------------------

export const eventGet = defineContract({
  name: 'sales.event.get',
  title: 'Veranstaltung anzeigen',
  description: 'Eine Veranstaltung mit Terminen, Ort, Positionen, Verfügbarkeit der Leihartikel und den Belegen des Vorgangs.',
  llm: {
    description:
      'Liefert eine Veranstaltung: Zustand, Kunde bzw. Interessent, Ort, Termine, Positionen (Mengen in Tausendstel), je Leihartikel die ' +
      'Verfügbarkeit im Zeitraum (verfügbar, knapp, nicht verfügbar – mit Fehlmenge), die Belege (Angebot, Auftrag, Kommissionslieferung, ' +
      'Abrechnung) und in `allowed`, was jetzt möglich ist. Verwenden für „Ist für die Hochzeit am Samstag alles frei?“.',
    keywords: ['Veranstaltung', 'Fest', 'Verfügbarkeit', 'Leihartikel frei', 'Stand'],
  },
  input: z.strictObject({ id: zId }),
  output: zEventView,
  permission: READ,
  feature: FEATURE,
  risk: 'read',
  scope: 'record',
})

export const EVENT_LIST_VIEWS = ['open', 'inquiries', 'upcoming', 'done', 'all'] as const

export const eventList = defineContract({
  name: 'sales.event.list',
  title: 'Veranstaltungen anzeigen',
  description: 'Veranstaltungen nach Zustand und Datum – offene zuerst nach Anlieferung, abgeschlossene zuletzt.',
  llm: {
    description:
      'Listet Veranstaltungen an den Standorten des Nutzers. `view`: open = nicht abgerechnet oder abgesagt (Standard), inquiries = nur ' +
      'Anfragen, upcoming = zugesagt und noch nicht geliefert, done = abgerechnet oder abgesagt, all = alle. `q` sucht in Nummer, Anlass, ' +
      'Kunde und Ort; `from`/`to` begrenzen auf Veranstaltungen, die den Zeitraum berühren. Zählt je Zustand mit.',
    keywords: ['Veranstaltungen', 'Feste', 'Anfragen', 'Festkalender', 'diese Woche'],
  },
  input: z.strictObject({
    view: z.enum(EVENT_LIST_VIEWS).default('open'),
    status: z.array(z.enum(EVENT_STATUSES)).max(7).optional(),
    q: z.string().trim().max(100).optional(),
    from: zDay.optional(),
    to: zDay.optional(),
    partnerId: zId.optional(),
    /** additiv: die Veranstaltung zu einem Beleg (Angebot, Auftrag, Kommissionslieferung, Abholauftrag, Abrechnung) */
    documentId: zId.optional(),
    limit: z.int().min(1).max(500).default(200),
  }),
  output: z.object({
    rows: z.array(zEventRow),
    counts: z.object({ inquiry: z.int(), quoted: z.int(), confirmed: z.int(), delivered: z.int(), picked_up: z.int(), settled: z.int(), cancelled: z.int(), open: z.int() }),
  }),
  permission: READ,
  feature: FEATURE,
  risk: 'read',
  scope: 'any',
})

export const eventCount = defineContract({
  name: 'sales.event.count',
  title: 'Offene Veranstaltungsanfragen zählen',
  description: 'Zählt Anfragen, die auf ein Angebot oder eine Zusage warten – für Navigation und Startseite.',
  llm: { description: 'Zählt die Veranstaltungen im Zustand „Anfrage“ an den Standorten des Nutzers. Für die Liste sales.event.list.', keywords: ['wie viele Anfragen', 'offene Anfragen'] },
  input: z.strictObject({}),
  output: z.object({ count: z.int() }),
  permission: READ,
  feature: FEATURE,
  risk: 'read',
  scope: 'any',
})

// ---- Ereignis -------------------------------------------------------------------------------------------------------

/** Eine Veranstaltung wurde angelegt oder hat ihren Zustand gewechselt (nach dem Festschreiben). */
export const eventChanged = defineEvent({
  name: 'sales.event.changed',
  title: 'Veranstaltung geändert',
  payload: z.object({ eventId: zId, number: z.string(), status: z.enum(EVENT_STATUSES), source: z.enum(EVENT_SOURCES) }),
})
